import { useCallback, useRef, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { submit, isValid, isDirty } from 'redux-form'

// Orchestrates saving several independent redux-form instances behind a single
// page-level "Save" button.
//
// Because each section form persists to the same underlying record (one PATCH
// per section), the forms are submitted *sequentially* so their responses can't
// race and clobber one another in the store. Only forms the user actually
// changed are submitted. If any dirty form is invalid, none are submitted so we
// never persist a partial set of changes; the invalid forms are still submitted
// remotely so their validation errors surface inline.
//
// Usage:
//   const { save, saving, formCallbacks } = useMultiFormSave()
//   // spread formCallbacks onto each orchestrated form:
//   <SomeForm onSubmit={...} {...formCallbacks} hideSubmit />
//   // trigger the combined save:
//   save({ formNames: [...], onComplete, onError })
export default function useMultiFormSave() {
  const dispatch = useDispatch()
  const store = useStore()
  const [saving, setSaving] = useState(false)
  // Only one form is ever in flight at a time, so a single resolver is enough.
  const pendingResolveRef = useRef(null)

  // Shared success/failure handlers spread onto every orchestrated form. They
  // resolve the promise awaited by the sequential submit loop below.
  const formCallbacks = useRef({
    onSubmitSuccess: (result) => {
      if (pendingResolveRef.current)
        pendingResolveRef.current({ ok: true, result })
    },
    // redux-form calls this with (errors, dispatch, submitError); capture the
    // errors so the caller can surface a meaningful failure message.
    onSubmitFail: (errors, _dispatch, submitError) => {
      if (pendingResolveRef.current)
        pendingResolveRef.current({ ok: false, errors, submitError })
    },
  }).current

  const submitOne = useCallback(
    (formName) =>
      new Promise((resolve) => {
        pendingResolveRef.current = resolve
        dispatch(submit(formName))
      }).finally(() => {
        pendingResolveRef.current = null
      }),
    [dispatch]
  )

  const save = useCallback(
    async ({ formNames, onComplete, onError }) => {
      if (saving) return
      const state = store.getState()

      // Only persist sections the user actually edited.
      const dirtyForms = formNames.filter((name) => isDirty(name)(state))
      if (dirtyForms.length === 0) {
        if (onComplete) onComplete(null)
        return
      }

      // Block the whole save if any edited section is invalid, but still submit
      // the invalid ones so their inline validation errors are displayed.
      const invalidForms = dirtyForms.filter((name) => !isValid(name)(state))
      if (invalidForms.length > 0) {
        invalidForms.forEach((name) => dispatch(submit(name)))
        if (onError) onError({})
        return
      }

      setSaving(true)
      // Submit sequentially by chaining promises, stopping at the first failure.
      const outcome = await dirtyForms.reduce(
        (chain, name) =>
          chain.then((acc) => {
            if (!acc.ok) return acc
            return submitOne(name).then(
              ({ ok, result, errors, submitError }) => ({
                ok,
                lastResult: result || acc.lastResult,
                errors,
                submitError,
              })
            )
          }),
        Promise.resolve({ ok: true, lastResult: null })
      )
      setSaving(false)

      if (!outcome.ok) {
        if (onError)
          onError({ errors: outcome.errors, submitError: outcome.submitError })
        return
      }
      if (onComplete) onComplete(outcome.lastResult)
    },
    [saving, store, dispatch, submitOne]
  )

  return { save, saving, formCallbacks }
}
